import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { finalize, ReplaySubject } from "rxjs";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatTooltipModule } from "@angular/material/tooltip";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { Router } from "@angular/router";
import { GestionclientesService } from "src/app/clientes/gestionclientes.service";
import { TicketsService } from "src/app/tickets/tickets.service";
import { Cliente } from "src/app/shared/model/ajustesUsuario/cliente.model";
import { ConsultaList } from "src/app/shared/model/core/ConsultaList.model";
import { Proyecto } from "src/app/shared/model/gestion-proyectos/proyecto";
import { Area } from "src/app/shared/model/tickets/area.model";
import { ProyectosService } from "../proyectos.service";

type AdminProyectosListState = {
  currentPage?: number;
  pageSize?: number;
  nomProyecto?: string;
  clientefl?: string;
  selectAreaId?: number | null;
  formAreaSearch?: string;
  formClienteSearch?: string;
  tipoFl?: string;
  estatusFl?: string;
};

@Component({
  selector: "app-adm-proyectos",
  standalone: true,
  templateUrl: "./adm-proyectos.component.html",
  styleUrls: ["./adm-proyectos.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    NgxMatSelectSearchModule,
  ],
})
export class AdmProyectosComponent implements OnInit {
  selectArea: Area | null = null;
  formArea = new FormControl("");
  filterArea = new ReplaySubject<Area[]>(1);
  consultaArea = new ConsultaList<Area>();
  listaAreas: Area[] = [];

  projects: Proyecto[] = [];
  nomProyecto = "";
  clientefl = "";
  tipoFl = "";
  estatusFl = "";
  formCliente = new FormControl("");
  listaClientes: Cliente[] = [];
  regPagCliente = 1000;
  requestFilter = new ConsultaList<Proyecto>();
  requestCliente = new ConsultaList<Cliente>();

  totalRegistros = 0;
  currentPage = 1;
  pageSize = 5;
  readonly pageSizeOptions = [5, 10, 20];
  loading = false;
  private readonly listStateStorageKey = "adm-proyectos:list-state";
  private pendingAreaId: number | null = null;

  constructor(
    private proyectoService: ProyectosService,
    private clientService: GestionclientesService,
    private ticketService: TicketsService,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.formArea.valueChanges.subscribe(() => this.filtraAreas());
    this.formCliente.valueChanges.subscribe(() => this.filtraCliente());

    this.requestCliente.regInicial = 0;
    this.requestCliente.regXPagina = this.regPagCliente;

    this.restoreListState();
    queueMicrotask(() => {
      this.consultaAreasActivos();
      this.consultarClientes();
      this.consultarProyectos(this.currentPage);
      this.cdr.detectChanges();
    });
  }

  consultarProyectos(page = 1): void {
    this.currentPage = Math.max(page, 1);
    this.loading = this.projects.length === 0;
    this.cdr.detectChanges();

    const busquedaProyecto = this.nomProyecto?.trim() ?? "";
    this.requestFilter.param1 = busquedaProyecto;
    this.requestFilter.param8 = busquedaProyecto;
    this.requestFilter.param2 = this.clientefl ?? "";
    this.requestFilter.param3 = this.tipoFl ?? "";
    this.requestFilter.param7 = this.estatusFl ?? "";
    this.requestFilter.param5 = this.selectArea?.id ?? null;
    this.requestFilter.param6 = 5;
    this.requestFilter.regInicial = (this.currentPage - 1) * this.pageSize;
    this.requestFilter.regXPagina = this.pageSize;
    this.requestFilter.pagina = this.currentPage - 1;

    this.proyectoService
      .consultaProyectoMongo(this.requestFilter)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response: any) => {
          this.projects = Array.isArray(response?.content) ? [...response.content] : [];
          this.totalRegistros = this.resolveTotalRegistros(response, this.projects.length);

          const maxPages = this.totalPages;
          if (maxPages > 0 && this.currentPage > maxPages) {
            this.consultarProyectos(maxPages);
            return;
          }

          this.cdr.detectChanges();
        },
        error: () => {
          this.projects = [];
          this.totalRegistros = 0;
          this.cdr.detectChanges();
        },
      });
  }

  openAddProyecto(): void {
    this.persistListState();
    this.router.navigate([`${this.getListRoute()}/alta`], {
      state: {
        listState: this.buildListState(),
      },
    });
  }

  openModProyecto(proyecto: Proyecto): void {
    const proyectoClonado = this.cloneProyecto(proyecto);
    this.persistListState();
    this.persistProjectSnapshot(proyectoClonado);
    this.router.navigate([`${this.getListRoute()}/edicion/${proyecto.id}`], {
      state: {
        proyecto: proyectoClonado,
        listState: this.buildListState(),
      },
    });
  }

  clearNombrePro(): void {
    this.nomProyecto = "";
    this.consultarProyectos(1);
  }

  clearNombreCli(): void {
    this.formCliente.setValue("");
    this.clientefl = "";
    this.requestFilter.param2 = null;
    this.consultarProyectos(1);
    this.consultarClientes();
  }

  clearArea(): void {
    this.formArea.setValue("");
    this.selectArea = null;
    this.requestFilter.param5 = null;
    this.consultaAreasActivos();
    this.consultarProyectos(1);
  }

  onTipoChange(): void {
    this.consultarProyectos(1);
  }

  clearTipo(): void {
    this.tipoFl = "";
    this.consultarProyectos(1);
  }

  onEstatusChange(): void {
    this.consultarProyectos(1);
  }

  clearEstatus(): void {
    this.estatusFl = "";
    this.consultarProyectos(1);
  }

  consultarClientes(): void {
    this.clientService.filtroBusquedaCliente(this.requestCliente).subscribe((res) => {
      this.listaClientes = Array.isArray(res?.list) ? res.list : [];
      this.cdr.detectChanges();
    });
  }

  filtraCliente(): void {
    const searchValue = (this.formCliente.value ?? "").toString().trim().toLowerCase();
    this.requestCliente.param1 = searchValue;

    this.clientService.filtroBusquedaCliente(this.requestCliente).subscribe((res) => {
      this.listaClientes = Array.isArray(res?.list) ? res.list : [];
      this.cdr.detectChanges();
    });
  }

  getArea(valor: Area | null): void {
    this.selectArea = valor;
    this.requestFilter.param5 = valor?.id ?? null;
    this.consultarProyectos(1);
  }

  consultaAreasActivos(): void {
    this.consultaArea.regInicial = 0;
    this.consultaArea.regFinal = 10000;

    this.ticketService.consultarAreaPaginacion(this.consultaArea).subscribe((res) => {
      if (res?.list?.length) {
        this.listaAreas = res.list;
        this.filterArea.next(this.listaAreas.slice());
      } else {
        this.listaAreas = [];
        this.filterArea.next([]);
      }

      if (this.pendingAreaId != null) {
        this.selectArea =
          this.listaAreas.find((area) => area.id === this.pendingAreaId) ?? null;
      }

      this.cdr.detectChanges();
    });
  }

  filtraAreas(): void {
    const searchValue = (this.formArea.value ?? "").toString().trim().toLowerCase();

    if (!searchValue) {
      this.filterArea.next(this.listaAreas.slice());
      return;
    }

    this.filterArea.next(
      this.listaAreas.filter((area) => area.area?.toLowerCase().includes(searchValue)),
    );
  }

  onPageSizeChange(value: number): void {
    this.pageSize = Number(value);
    this.consultarProyectos(1);
  }

  onPageChange(page: number): void {
    if (page === this.currentPage || page < 1 || page > this.totalPages) {
      return;
    }
    this.consultarProyectos(page);
  }

  firstPage(): void {
    this.onPageChange(1);
  }

  previousPage(): void {
    this.onPageChange(this.currentPage - 1);
  }

  nextPage(): void {
    this.onPageChange(this.currentPage + 1);
  }

  lastPage(): void {
    this.onPageChange(this.totalPages);
  }

  get totalPages(): number {
    return this.totalRegistros > 0 ? Math.ceil(this.totalRegistros / this.pageSize) : 0;
  }

  get showPagination(): boolean {
    return this.totalRegistros > 0;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRegistros);
  }

  getProjectTypeLabel(proyecto: Proyecto): string {
    return proyecto?.tipo || "PROYECTO";
  }

  getProjectTypeClass(proyecto: Proyecto): string {
    return proyecto?.tipo === "OPORTUNIDAD" ? "is-pending" : "is-active";
  }

  getProjectIdentifier(proyecto: Proyecto): string {
    return proyecto?.idProyecto || proyecto?.id?.toString() || "-";
  }

  getProjectShortName(proyecto: Proyecto): string {
    return proyecto?.nombre_corto || "Sin nombre corto";
  }

  getLeaderName(proyecto: Proyecto): string {
    return proyecto?.nombreLider || "Sin líder asignado";
  }

  getDateRangeLabel(proyecto: Proyecto): string {
    const start = this.formatDate(proyecto?.fechaInicio);
    const end = this.formatDate(proyecto?.fechaFin);

    if (!start && !end) {
      return "Sin fechas definidas";
    }

    if (!start || !end) {
      return `${start || "Sin inicio"} / ${end || "Sin fin"}`;
    }

    return `${start} / ${end}`;
  }

  getPaginationItems(): Array<number | string> {
    if (this.totalPages <= 1) {
      return [1];
    }

    const pages = new Set<number>([
      1,
      this.totalPages,
      this.currentPage - 1,
      this.currentPage,
      this.currentPage + 1,
    ]);

    const sortedPages = Array.from(pages)
      .filter((page) => page >= 1 && page <= this.totalPages)
      .sort((a, b) => a - b);

    const items: Array<number | string> = [];
    for (let index = 0; index < sortedPages.length; index++) {
      const page = sortedPages[index];
      const previous = sortedPages[index - 1];
      if (previous && page - previous > 1) {
        items.push(`ellipsis-${previous}-${page}`);
      }
      items.push(page);
    }

    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === "number";
  }

  formatDate(date: Date | string | null | undefined): string {
    if (!date) {
      return "";
    }

    const parsedDate = typeof date === "string" ? new Date(date) : date;
    if (!(parsedDate instanceof Date) || Number.isNaN(parsedDate.getTime())) {
      return "";
    }

    return parsedDate.toLocaleDateString();
  }

  private resolveTotalRegistros(response: any, currentItems: number): number {
    const candidates = [
      response?.totalElements,
      response?.pTotalItems,
      response?.totalItems,
      response?.totalRegistros,
    ];

    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed >= 0) {
        return parsed;
      }
    }

    if (currentItems === 0) {
      return 0;
    }

    return (this.currentPage - 1) * this.pageSize + currentItems;
  }

  private restoreListState(): void {
    const listState =
      (history.state?.listState as AdminProyectosListState | undefined) ??
      this.readStoredListState();
    if (!listState) {
      return;
    }

    const restoredPage = Number(listState.currentPage);
    if (Number.isFinite(restoredPage) && restoredPage > 0) {
      this.currentPage = restoredPage;
    }

    const restoredPageSize = Number(listState.pageSize);
    if (
      Number.isFinite(restoredPageSize) &&
      this.pageSizeOptions.includes(restoredPageSize)
    ) {
      this.pageSize = restoredPageSize;
    }

    if (typeof listState.nomProyecto === "string") {
      this.nomProyecto = listState.nomProyecto;
    }

    if (typeof listState.clientefl === "string") {
      this.clientefl = listState.clientefl;
    }

    if (typeof listState.tipoFl === "string") {
      this.tipoFl = listState.tipoFl;
    }

    if (typeof listState.estatusFl === "string") {
      this.estatusFl = listState.estatusFl;
    }

    const restoredAreaId = Number(listState.selectAreaId);
    if (Number.isFinite(restoredAreaId) && restoredAreaId > 0) {
      this.pendingAreaId = restoredAreaId;
      this.requestFilter.param5 = restoredAreaId;
    }

    if (typeof listState.formAreaSearch === "string") {
      this.formArea.setValue(listState.formAreaSearch, { emitEvent: false });
    }

    if (typeof listState.formClienteSearch === "string") {
      this.formCliente.setValue(listState.formClienteSearch, {
        emitEvent: false,
      });
      this.requestCliente.param1 = listState.formClienteSearch;
    }
  }

  private buildListState(): AdminProyectosListState {
    return {
      currentPage: this.currentPage,
      pageSize: this.pageSize,
      nomProyecto: this.nomProyecto,
      clientefl: this.clientefl,
      tipoFl: this.tipoFl,
      estatusFl: this.estatusFl,
      selectAreaId: this.selectArea?.id ?? this.pendingAreaId ?? null,
      formAreaSearch: (this.formArea.value ?? "").toString(),
      formClienteSearch: (this.formCliente.value ?? "").toString(),
    };
  }

  private persistListState(): void {
    sessionStorage.setItem(
      this.listStateStorageKey,
      JSON.stringify(this.buildListState()),
    );
  }

  private readStoredListState(): AdminProyectosListState | null {
    const rawState = sessionStorage.getItem(this.listStateStorageKey);
    if (!rawState) {
      return null;
    }

    try {
      return JSON.parse(rawState) as AdminProyectosListState;
    } catch {
      sessionStorage.removeItem(this.listStateStorageKey);
      return null;
    }
  }

  private getListRoute(): string {
    const rolePrefix = this.router.url.split("/")[1] || "ODP";
    return `/${rolePrefix}/admproyectos`;
  }

  private cloneProyecto(proyecto: Proyecto): Proyecto {
    return JSON.parse(JSON.stringify(proyecto)) as Proyecto;
  }

  private persistProjectSnapshot(proyecto: Proyecto): void {
    const projectId = proyecto?.id;
    if (projectId == null) {
      return;
    }

    sessionStorage.setItem(
      this.getProjectStorageKey(projectId),
      JSON.stringify(proyecto),
    );
  }

  private getProjectStorageKey(projectId: number | string): string {
    const rolePrefix = this.router.url.split("/")[1] || "ODP";
    return `adm-proyectos:${rolePrefix}:project:${projectId}`;
  }
}
